import {
    RiArrowRightSLine, RiArticleLine, RiIdCardLine, RiInfoCardLine, RiInformation2Line, RiInformationLine, RiMoreLine, RiPriceTagLine,
    RiQuestionAnswerLine,
} from "@remixicon/react";
import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import {
    Article, ArticleTag, ArticleType, GetArticleTagsDocument,
    useDeleteArticleTagMutation, useGetArticlesByTagQuery, useGetArticleTagsQuery,
    useSaveArticleTagMutation,
} from "../../../../__generated__/api";
import { useConfirm } from "../../../../components/confirm/Confirm";
import useDeleteConfirm from "../../../../components/confirm/Delete";
import { useProxyProperty } from "../../../../components/dirty-tracker/FormTracker";
import Button from "../../../../components/theme/button/Button";
import RouterLink from "../../../../components/theme/button/RouterLink";
import Dropdown from "../../../../components/theme/dropdown/Dropdown";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import Icon from "../../../../components/theme/icon/Icon";
import Text from "../../../../components/theme/text/Text";
import { Paths, useParams } from "../../../../contexts/Routing";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import Styles from "./ArticleTags.module.scss";
import Portal from "../../../../components/portal/Portal";

const MAX_INDENT = 15;
interface ArticleTagView {
    tag: ArticleTag;
    children: ArticleTagView[];
}

interface TagActions {
    onAddCategory: (parentId?: string) => void;
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
    isReadOnly?: boolean;
}

function createSortString<T>(extract: ((item: T) => string)) {
    return (left: T, right: T) => {
        const leftValue = extract(left);
        const rightValue = extract(right);
        return leftValue.localeCompare(rightValue);
    };
}
function createTags(tags: ArticleTag[]): ArticleTagView[] {
    const tagIds = new Set(tags.flatMap(group => group.childIds).map(row => row.toString()));
    const tagMap = tags.toIdMap("id");
    const roots = tags.filter(row => !tagIds.has(row.id));

    const createChildren = (tag: ArticleTag): ArticleTagView => ({
        tag,
        children: tag.childIds.map(id => tagMap[id])
            .filterNull()
            .map(row => createChildren(row))
            .copy<ArticleTagView>()
            .sort(createSortString(row => row.tag.title)),
    });

    return roots.map(tag => createChildren(tag));
}

function ArticleTagTitle({
    tagId, title, onExpanded, className, actions, indentIndex,
}: {
    tagId: string;
    title: string
    onExpanded: () => void;
    className: string;
    actions: TagActions;
    indentIndex: number
}) {
    return (
        <div className={classNames(Styles["tag-title"], className)}>
            <Icon icon={<RiPriceTagLine />} className={Styles.icon} />
            <Button
                prefixIcon={<Icon icon={<RiArrowRightSLine />} />}
                size="xsmall"
                variant="secondary"
                onClick={onExpanded}
                className={Styles["tag-title-icon"]}
            />
            <Button className={Styles["tag-title-text"]} onClick={onExpanded} variant="none">
                {title}
            </Button>
            {!actions.isReadOnly && (
                <Dropdown>
                    <Dropdown.Toggle
                        className={Styles["tag-title-icon"]}
                        size="xsmall"
                        variant="secondary"
                        prefixIcon={<Icon icon={<RiMoreLine />} />}
                    />
                    <Portal>
                        <Dropdown.Menu>
                            <Dropdown.Link to={Paths.ANSWERS_NEW} search={{ tagId }} prefixIcon={<Icon.Add />}>
                                Add New Page
                            </Dropdown.Link>
                            {indentIndex < MAX_INDENT && (
                                <Dropdown.Item
                                    prefixIcon={<Icon icon={<RiPriceTagLine />} />}
                                    onClick={() => actions.onAddCategory(tagId)}
                                >
                                    Add Category
                                </Dropdown.Item>
                            )}
                            <Dropdown.Item prefixIcon={<Icon.Edit />} onClick={() => actions.onEdit(tagId)}>
                                Edit
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item prefixIcon={<Icon.Delete variant="danger" />} onClick={() => actions.onDelete(tagId)}>
                                <Text colorVariant="danger" textVariant="body-2-medium">Delete</Text>
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Portal>
                </Dropdown>
            )}

        </div>
    );
}

function ArticleTagDisplay({
    tag, isReadOnly, isExpandedSet,
    onChangeExpanded,
    prefixIndent,
    actions,
}: {
    tag: ArticleTagView;
    isReadOnly: boolean;
    isExpandedSet: Set<string>;
    onChangeExpanded: (value: Set<string>) => void;
    prefixIndent: number
    actions: TagActions
}) {
    return (
        <div className={classNames(Styles["tag-container"])}>
            <ArticleTagTitle
                tagId={tag.tag.id}
                title={tag.tag.title}
                className={Styles[`indent-${Math.min(prefixIndent, MAX_INDENT)}`]}
                actions={actions}
                indentIndex={prefixIndent}
                onExpanded={() => {
                    if (isExpandedSet.has(tag.tag.id)) {
                        isExpandedSet.delete(tag.tag.id);
                    } else {
                        isExpandedSet.add(tag.tag.id);
                    }
                    onChangeExpanded(isExpandedSet);
                }}
            />
            {isExpandedSet.has(tag.tag.id) && tag.children && (
                <div className={Styles["tag-children"]}>
                    {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
                    <ArticleTagsList
                        onChangeExpanded={onChangeExpanded}
                        tag={tag}
                        actions={actions}
                        isReadOnly={isReadOnly}
                        isExpandedSet={isExpandedSet}
                        prefixIndent={prefixIndent + 1}
                    />
                </div>
            )}
        </div>
    );
}

function ArticleTagsList({
    tag, isReadOnly, isExpandedSet, onChangeExpanded, prefixIndent, actions,
}: {
    tag: ArticleTagView;
    isReadOnly: boolean;
    isExpandedSet: Set<string>;
    onChangeExpanded: (value: Set<string>) => void;
    prefixIndent: number
    actions: TagActions

}) {
    const { id } = useParams(Paths.ANSWERS_GET);
    const { data: articlesByTag, loading } = useGetArticlesByTagQuery({
        skip: tag?.tag == null,
        variables: {
            tagId: tag?.tag.id,
        },
    });

    return (
        <>
            {tag?.children?.copy<ArticleTagView>().sort(createSortString(row => row.tag.title)).filterMap(childTag => (
                <ArticleTagDisplay
                    key={childTag.tag.id}
                    tag={childTag}
                    isReadOnly={isReadOnly}
                    isExpandedSet={isExpandedSet}
                    onChangeExpanded={onChangeExpanded}
                    prefixIndent={prefixIndent}
                    actions={actions}
                />
            ))}
            {articlesByTag?.articles.results.copy<Article>().sort(createSortString(row => row.title)).map(article => (
                <RouterLink
                    to={Paths.ANSWERS_GET}
                    params={{ id: article.id }}
                    className={classNames(
                        Styles["tag-article"],
                        Styles[`indent-${Math.min(prefixIndent, MAX_INDENT)}`],
                        id === article.id ? Styles["tag-article-active"] : "",
                    )}
                    key={article.id}
                    variant="none"
                    prefixIcon={article.type === ArticleType.Knowledge ? <RiInfoCardLine /> : <RiArticleLine />}
                >
                    {article.title || <Text as="span" colorVariant="body-secondary">Untitled</Text>}
                </RouterLink>
            ))}
            {!actions.isReadOnly && (
                <RouterLink
                    to={Paths.ANSWERS_GET}
                    params={{ id: "new" }}
                    search={{ tagId: tag.tag.id }}
                    className={classNames(Styles["new-page"], Styles[`indent-${Math.min(prefixIndent, MAX_INDENT)}`])}
                    variant="none"
                    size="small"
                    prefixIcon={<Icon.Add />}
                >
                    New Page
                </RouterLink>
            )}

        </>
    );
}

const IS_EXPANDED_KEY = "answers-expanded";

function getExpandedSet(tags?: ArticleTag[]): string[] {
    const value = localStorage.getItem(IS_EXPANDED_KEY);
    const expandedList = value ? JSON.parse(value) : [];
    if (tags == null) {
        return expandedList;
    }
    const tagIds = tags.toIdList("id");
    return [...expandedList, ...tagIds] as string[];
}

function storeExpanded(isExpandedSet: Set<string>) {
    localStorage.setItem(IS_EXPANDED_KEY, JSON.stringify(Array.from(isExpandedSet)));
}

function findAllParents(tagsOnArticle: ArticleTag[], allTags: ArticleTag[], parentTagsToExpand: ArticleTag[] = []): ArticleTag[] {
    if (tagsOnArticle.isEmpty || allTags.isEmpty) {
        return parentTagsToExpand;
    }
    const selectedIds = new Set(tagsOnArticle.toIdList("id"));
    const parentTags = allTags?.filter(tag => tag.childIds?.some(child => selectedIds.has(child))) as ArticleTag[];

    if (parentTags.isNotEmpty) {
        parentTagsToExpand.push(...parentTags);
        return findAllParents(parentTags, allTags, parentTagsToExpand);
    }
    return parentTagsToExpand;
}

export default function ArticleTags({ article, isReadOnly }: {article: Article | undefined, isReadOnly: boolean}) {
    const { data, loading } = useGetArticleTagsQuery();

    const tagTree = useMemo(() => (data?.articleTags ? createTags(data?.articleTags) : undefined), [data?.articleTags]);

    const [isExpandedSet, setExpanded] = useState(() => new Set(getExpandedSet()));

    const [deleteTagAction] = useDeleteArticleTagMutation();
    const [saveTag] = useSaveArticleTagMutation();

    useEffect(() => {
        if (data?.articleTags && article?.tags) {
            const startTags = article?.tags.limit(1);
            const parents = startTags ? findAllParents(startTags, data?.articleTags ?? []) : [];
            setExpanded(new Set(getExpandedSet([...parents, ...startTags])));
        }
    }, [data?.articleTags, article?.tags]);

    const { element: deleteElement, setDeleting } = useDeleteConfirm<ArticleTag>(item => {
        LoggingHandler.instance.wrap(async () => {
            await deleteTagAction({
                refetchQueries: [
                    { query: GetArticleTagsDocument },
                ],
                variables: {
                    id: item.id,
                },
            });
        });
    }, item => item.title);

    const proxy = useProxyProperty<ArticleTag>(() => ({} as ArticleTag), []);
    const properties = proxy.useBuilder(handler => handler
        .property("id", value => value.id)
        .property("title", value => value.title)
        .property("description", value => value.description)
        .property("childIds", value => value.childIds)
        .property<"parentId", unknown>("parentId", () => undefined));

    const clear = () => {
        properties.id.setValue(undefined);
        properties.title.setValue("");
        properties.description.setValue("");
        properties.childIds.setValue([]);
        properties.parentId.setValue(undefined);
    };
    const { element: editElement, setShouldShow } = useConfirm({
        title: properties.id.value ? "Edit Category" : "Add Category",
        confirmText: "Save",
        onHide: () => {
            clear();
        },
        onConfirm: async () => {
            await saveTag({
                variables: {
                    parentId: properties.parentId.value,
                    id: properties.id.value,
                    articleTag: {
                        title: properties.title.value,
                        description: properties.description.value ?? "",
                        childIds: properties.childIds.value ?? [],
                        iconUrl: "",
                    },
                },
                refetchQueries: [
                    { query: GetArticleTagsDocument },
                ],
            });
            clear();
        },
        body: (
            <div className={Styles["edit-tag"]}>
                <FormGroup
                    label="Title"
                    value={properties.title.value ?? ""}
                    onChange={properties.title.setValueFromInput}
                    isRequired
                    autoFocus
                />
                <FormGroup
                    label="Description"
                    type="text"
                    display="textarea"
                    value={properties.description.value ?? ""}
                    onChange={properties.description.setValueFromInput}
                />

            </div>
        ),
    });

    const actions = useMemo<TagActions>(() => ({
        onAddCategory: (parentId?: string) => {
            proxy.setValue({
                id: undefined,
                title: "",
                description: "",
                childIds: [],
            } as ArticleTag);
            if (parentId) {
                properties.parentId.setValue(parentId);
            }
            setShouldShow(true);
        },
        onEdit: (id: string) => {
            const item = data?.articleTags?.find(row => row.id === id);
            if (item) {
                proxy.setValue(item);
                setShouldShow(true);
            }
        },
        onDelete: (id: string) => {
            const item = data?.articleTags?.find(row => row.id === id);
            if (item) {
                setDeleting(item);
            }
        },
        isReadOnly,
    }), [tagTree, isReadOnly]);

    return (
        <>
            {!isReadOnly && (
                <div className={Styles["add-bar"]}>
                    <Button.Add
                        variant="none"
                        onClick={() => actions.onAddCategory()}
                        className={Styles["add-btn"]}
                    >
                        Add Category
                    </Button.Add>
                </div>
            )}

            <div className={Styles.tags}>
                {tagTree?.copy<ArticleTagView>().sort(createSortString(row => row.tag.title)).filterMap(tag => (
                    <div className={Styles["tag-section"]} key={tag.tag.id}>
                        <ArticleTagTitle
                            tagId={tag.tag.id}
                            title={tag.tag.title}
                            className={Styles["indent-0"]}
                            actions={actions}
                            indentIndex={0}
                            onExpanded={() => {
                                const copy = new Set(isExpandedSet);
                                if (copy.has(tag.tag.id)) {
                                    copy.delete(tag.tag.id);
                                } else {
                                    copy.add(tag.tag.id);
                                }
                                storeExpanded(copy);
                                setExpanded(copy);
                            }}
                        />
                        {isExpandedSet.has(tag.tag.id) && (
                            <div className={Styles["tag-children"]}>
                                <ArticleTagsList
                                    tag={tag}
                                    isReadOnly={false}
                                    isExpandedSet={isExpandedSet}
                                    prefixIndent={1}
                                    actions={actions}
                                    onChangeExpanded={() => {
                                        storeExpanded(isExpandedSet);
                                        setExpanded(new Set(isExpandedSet));
                                    }}
                                />
                            </div>
                        )}
                    </div>
                ), tag => tag != null)}
            </div>
            {deleteElement}
            {editElement}

        </>

    );
}
